import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { stickFieldTemplatesApi } from "api/stickFieldTemplates";

import { useSelectedProject } from "components/project/projects/hooks";

import { ScenarioField } from "../types/field";
import { StickFieldsTemplate } from "../types/template";

const QUERY_KEY = "stickFieldsTemplates";

interface UseStickFieldTemplatesReturn {
  templates: StickFieldsTemplate[];
  isLoading: boolean;
  saveTemplate: (
    name: string,
    fields: ScenarioField[],
    projectId: string
  ) => Promise<StickFieldsTemplate>;
  updateTemplate: (id: string, fields: ScenarioField[]) => Promise<void>;
  renameTemplate: (id: string, newName: string) => Promise<void>;
  deleteTemplate: (id: string) => Promise<void>;
}

export function useStickFieldTemplates(): UseStickFieldTemplatesReturn {
  const queryClient = useQueryClient();
  const { selectedProject } = useSelectedProject();
  const projectId = selectedProject?.projectId;

  const { data: templates = [], isLoading } = useQuery(
    [QUERY_KEY, projectId],
    () => stickFieldTemplatesApi.getByProject(projectId as string),
    { enabled: !!projectId, staleTime: 60_000 }
  );

  const projectIdRef = useRef(projectId);
  useEffect(() => {
    projectIdRef.current = projectId;
  }, [projectId]);

  const invalidate = (targetProjectId?: string) => {
    queryClient.invalidateQueries([QUERY_KEY, targetProjectId ?? projectIdRef.current]);
  };

  const createMutation = useMutation(stickFieldTemplatesApi.create, {
    onSuccess: (_, vars) => invalidate(vars.projectId)
  });

  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: { name?: string; fields?: ScenarioField[] } }) =>
      stickFieldTemplatesApi.update(id, data),
    { onSuccess: () => invalidate() }
  );

  const deleteMutation = useMutation(stickFieldTemplatesApi.delete, {
    onSuccess: () => invalidate()
  });

  const saveTemplate = (
    name: string,
    fields: ScenarioField[],
    targetProjectId: string
  ): Promise<StickFieldsTemplate> =>
    createMutation.mutateAsync({ name, fields, projectId: targetProjectId });

  const updateTemplate = (id: string, fields: ScenarioField[]): Promise<void> =>
    updateMutation.mutateAsync({ id, data: { fields } });

  const renameTemplate = (id: string, newName: string): Promise<void> =>
    updateMutation.mutateAsync({ id, data: { name: newName } });

  const deleteTemplate = (id: string): Promise<void> => deleteMutation.mutateAsync(id);

  return {
    templates,
    isLoading,
    saveTemplate,
    updateTemplate,
    renameTemplate,
    deleteTemplate
  };
}
